import { Controller } from "@hotwired/stimulus"

// § 3.5 Tier 3 — Forge Viewer (APS LMV) Stimulus controller.
//
// Loads the SDK from Autodesk's CDN (self-hosting the bundle is
// forbidden by APS ToS), wires the standard extension list, mints a
// `viewables:read`-scoped token via the server endpoint
// (`viewer_token.json`) so the customer's full-scope OAuth token never
// crosses into the browser (Architecture Decision #4), and proxies
// viewpoint save/restore + element selection + discipline visibility
// toggles back to the controller surface in this file.
//
// What this controller deliberately does NOT do:
//   * Cache the access token across navigations — every page load
//     re-mints to keep the security boundary plain.
//   * Self-host any APS asset.
//   * Persist `dbId` server-side. Saved viewpoints round-trip object
//     selections through `model.getExternalIdMapping` so they survive
//     re-translates (Architecture Decision #3).
const VIEWER_VERSION = "7.103" // pinned — bump after re-reading APS release notes
const VIEWER_BASE = "https://developer.api.autodesk.com/modelderivative/v2/viewers"
const STANDARD_EXTENSIONS = [
  "Autodesk.DocumentBrowser",
  "Autodesk.ModelStructure",
  "Autodesk.LayerManager",
  "Autodesk.PropertiesManager",
  "Autodesk.Section",
  "Autodesk.Measure",
  "Autodesk.Explode",
  "Autodesk.AEC.LevelsExtension",
  "Autodesk.AEC.Minimap3DExtension",
  "Autodesk.BimWalk"
]

// § 3.5 Tier 4 — custom toolbar extension (conditional on
// `canCreateActionItems`). The icon is a small inline SVG so the
// toolbar doesn't need a CDN round-trip; the click handler bridges
// back into the Stimulus controller via the closure created in
// `_registerCustomExtensions`.
const ACTION_ITEM_AUTHORING_EXTENSION = "Conslog.ActionItemAuthoring"
const ACTION_ITEM_AUTHORING_BUTTON_ID = "conslogActionItemAuthoringButton"
const ACTION_ITEM_AUTHORING_ICON_DATA_URL =
  "url('data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%23fff\" stroke-width=\"1.8\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z\"/><circle cx=\"12\" cy=\"10\" r=\"2.5\"/></svg>"
  ) +
  "')"

export default class extends Controller {
  static targets = [
    "container", "status", "statusTitle", "statusBody", "retryButton",
    "viewpointList", "viewpointName", "viewpointPreview", "viewpointThumb",
    "saveModal", "saveButton", "confirmSaveButton",
    "selectionPanel", "selectionCount"
  ]
  static values = {
    tokenUrl: String,
    sessionUrl: String,
    viewpointsUrl: String,
    encodedUrn: String,
    region: String,
    defaultViewGuid: String,
    csrfToken: String,
    canSaveViewpoints: Boolean,
    // § 3.5 Tier 4 — endpoint the "Create Action Item from 3D" button
    // POSTs to with the captured selection + viewer state + screenshot.
    // Empty string disables the toolbar button (e.g., a viewer where
    // the user can't author action items on this project).
    pinPrepareUrl: String,
    canCreateActionItems: Boolean
  }

  connect() {
    this._viewer = null
    this._loadedDocument = null
    this._currentModel = null
    this._sessionTimer = null
    this._sessionId = null
    this._lastSelectionDbId = null
    this._pendingScreenshotDataUrl = null
    this._onVisibilityChange = this._onVisibilityChange.bind(this)
    this._onRestoreViewpointEvent = this._onRestoreViewpointEvent.bind(this)
    document.addEventListener("visibilitychange", this._onVisibilityChange)
    // Tier 4: pin overlay sibling controller signals viewpoint
    // restoration via a custom event so this controller stays the
    // single owner of the SDK's restoreState path (event-wait,
    // dbId map invalidation, etc.).
    this.element.addEventListener("forge:restore-viewpoint", this._onRestoreViewpointEvent)
    this._bootSdk()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    this.element.removeEventListener("forge:restore-viewpoint", this._onRestoreViewpointEvent)
    this._stopSessionTimer({ final: true })
    // Clear the cross-controller handles so a new mount starts fresh.
    this.element._forgeViewer = null
    this.element._forgeViewerModel = null
    if (this._viewer) {
      try { this._viewer.finish() } catch (_) {}
      this._viewer = null
    }
  }

  // ── SDK boot + load ──────────────────────────────────────────────────

  async _bootSdk() {
    try {
      await this._loadSdkScript()
      await this._loadSdkStylesheet()
    } catch (err) {
      console.error("[forge-viewer] SDK load failed:", err)
      this._showError(
        "Autodesk Viewer is temporarily unavailable.",
        "We couldn't load the 3D viewer SDK from Autodesk. Retry, or open the model in ACC web app."
      )
      return
    }
    this._initViewer()
  }

  _loadSdkScript() {
    return new Promise((resolve, reject) => {
      if (window.Autodesk && window.Autodesk.Viewing) return resolve()
      const existing = document.querySelector(`script[data-forge-sdk]`)
      if (existing) {
        existing.addEventListener("load", () => resolve())
        existing.addEventListener("error", reject)
        return
      }
      const s = document.createElement("script")
      s.src = `${VIEWER_BASE}/${VIEWER_VERSION}/viewer3D.min.js`
      s.async = true
      s.dataset.forgeSdk = "true"
      s.addEventListener("load", () => resolve())
      s.addEventListener("error", () => reject(new Error("viewer3D.min.js failed")))
      document.head.appendChild(s)
    })
  }

  _loadSdkStylesheet() {
    return new Promise((resolve) => {
      if (document.querySelector(`link[data-forge-sdk-css]`)) return resolve()
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = `${VIEWER_BASE}/${VIEWER_VERSION}/style.min.css`
      link.dataset.forgeSdkCss = "true"
      link.addEventListener("load", () => resolve())
      link.addEventListener("error", () => resolve()) // CSS failure is non-fatal
      document.head.appendChild(link)
    })
  }

  _initViewer() {
    const Viewing = window.Autodesk && window.Autodesk.Viewing
    if (!Viewing) {
      this._showError("Viewer SDK missing", "Refresh and try again.")
      return
    }

    const options = {
      env: "AutodeskProduction2",
      api: "streamingV2",
      getAccessToken: (onSuccess) => this._fetchToken(onSuccess)
    }

    Viewing.Initializer(options, () => {
      this._setStatus("Streaming model…", "Loading geometry from Autodesk.")
      this._registerCustomExtensions(Viewing)
      const isMobile = this._isMobileViewport()
      // § 3.5 Tier 7 Step 78 — on narrow viewports (≤768px), drop the
      // desktop-first GuiViewer3D toolbar and run the headless
      // `Viewer3D` instead. The bottom-sheet markup on the show page
      // owns viewpoint navigation + the "Create Action Item" CTA on
      // mobile; the headless viewer keeps just the canvas + camera
      // gestures, which read better on touch.
      const extensions = STANDARD_EXTENSIONS.slice()
      if (this._canAuthorActionItems() && !isMobile) extensions.push(ACTION_ITEM_AUTHORING_EXTENSION)
      const ViewerCtor = isMobile ? (Viewing.Viewer3D || Viewing.GuiViewer3D) : Viewing.GuiViewer3D
      this._viewer = new ViewerCtor(this.containerTarget, { extensions })
      this._mobileViewer = isMobile
      this.element.classList.toggle("forge-viewer-mobile", isMobile)
      const code = this._viewer.start()
      if (code > 0) {
        this._showError("Viewer failed to start", "Try refreshing the page.")
        return
      }
      this._loadDocument()
    })
  }

  // § 3.5 Tier 7 Step 78 — viewport detection. Re-evaluating on resize
  // would let the viewer flip between Gui / headless mid-session,
  // which the SDK does not support cleanly — pin the choice at boot
  // and let the user reload after a window-size change.
  _isMobileViewport() {
    if (typeof window === "undefined" || !window.matchMedia) return false
    return window.matchMedia("(max-width: 768px)").matches
  }

  // § 3.5 Tier 4 — register the `Conslog.ActionItemAuthoring`
  // extension once per page lifecycle. Re-registering is safe (the SDK
  // returns the existing class) but we early-out to avoid the dev-mode
  // warning. The extension is small enough to live inline; if Tier 8
  // adds clash-detection or another authoring surface we'll hoist into
  // its own bundle.
  _registerCustomExtensions(Viewing) {
    if (this._extensionsRegistered) return
    if (!Viewing.theExtensionManager || !Viewing.Extension) {
      // Older SDKs surfaced both via `Viewing` directly; nothing to do.
      return
    }
    const controller = this
    const ExtBase = Viewing.Extension
    class ActionItemAuthoring extends ExtBase {
      load() {
        this._onToolbarCreated = this._onToolbarCreated.bind(this)
        if (this.viewer.toolbar) {
          this._addButton()
        } else {
          this.viewer.addEventListener(Viewing.TOOLBAR_CREATED_EVENT, this._onToolbarCreated)
        }
        return true
      }
      unload() {
        try {
          if (this._group && this.viewer.toolbar) this.viewer.toolbar.removeControl(this._group)
          this.viewer.removeEventListener(Viewing.TOOLBAR_CREATED_EVENT, this._onToolbarCreated)
        } catch (_) {}
        return true
      }
      _onToolbarCreated() { this._addButton() }
      _addButton() {
        if (this._button) return
        const Av = Viewing.UI || (window.Autodesk && window.Autodesk.Viewing && window.Autodesk.Viewing.UI)
        if (!Av || !Av.Button) return
        const btn = new Av.Button(ACTION_ITEM_AUTHORING_BUTTON_ID)
        btn.setToolTip("Create Action Item from 3D")
        btn.addClass("conslog-action-item-button")
        btn.icon.style.backgroundImage = ACTION_ITEM_AUTHORING_ICON_DATA_URL
        btn.icon.style.backgroundSize = "20px 20px"
        btn.icon.style.backgroundRepeat = "no-repeat"
        btn.icon.style.backgroundPosition = "center"
        btn.onClick = () => controller._handleCreateActionItemFromViewer()
        const group = new Av.ControlGroup("conslogGroup")
        group.addControl(btn)
        this.viewer.toolbar.addControl(group)
        this._group = group
        this._button = btn
      }
    }
    Viewing.theExtensionManager.registerExtension(ACTION_ITEM_AUTHORING_EXTENSION, ActionItemAuthoring)
    this._extensionsRegistered = true
  }

  async _fetchToken(onSuccess) {
    try {
      const res = await fetch(this.tokenUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (this.hasRegionValue && json.env) {
        // Region is pinned at mint time; the env on the response is
        // already region-aware. We don't override Initializer.env at
        // runtime — too late by then — but stash it for future loads.
        this._regionEnv = json.env
      }
      onSuccess(json.access_token, json.expires_in || 3600)
    } catch (err) {
      console.error("[forge-viewer] token fetch failed:", err)
      this._showError(
        "Can't authenticate with Autodesk",
        "We couldn't mint a viewer token. Refresh the page."
      )
    }
  }

  _loadDocument() {
    const urn = `urn:${this.encodedUrnValue}`
    const Viewing = window.Autodesk.Viewing
    Viewing.Document.load(
      urn,
      (doc) => {
        this._loadedDocument = doc
        const root = doc.getRoot()
        let node = null
        if (this.hasDefaultViewGuidValue && this.defaultViewGuidValue) {
          node = root.findByGuid(this.defaultViewGuidValue)
        }
        if (!node) node = root.getDefaultGeometry()
        if (!node) {
          this._showError("Model has no viewable geometry", "Re-translate the model from the BIM index.")
          return
        }
        this._loadModelNode(node)
      },
      (errCode, errMsg) => {
        console.error("[forge-viewer] Document.load failed:", errCode, errMsg)
        this._showError(
          "Couldn't open the model",
          "Autodesk reports the model isn't streamable. Try re-translating from the BIM index."
        )
      }
    )
  }

  _loadModelNode(node) {
    // Each `loadDocumentNode` call hands back a fresh dbId table, so
    // any cached mapping from the prior view must be invalidated to
    // keep externalId round-trips honest (Architecture Decision #3).
    this._dbIdMap = null
    this._viewer.loadDocumentNode(this._loadedDocument, node)
      .then((model) => {
        this._currentModel = model
        this._hideStatus()
        this._wireSelectionEvents()
        this._startSessionTimer()
        this._sendSessionTick(0) // initial open marker
        // Tier 4: expose the viewer + model on the root element so
        // the sibling `bim-pin-overlay` controller can wire up.
        // Setting both atomically before announcing the change keeps
        // the overlay's `_tryWireToViewer` poll from seeing a stale
        // model.
        this.element._forgeViewer = this._viewer
        this.element._forgeViewerModel = model
        const overlay = this.element._bimPinOverlay
        if (overlay && typeof overlay.reloadAfterViewSwap === "function") {
          overlay.reloadAfterViewSwap()
        }
      })
      .catch((err) => {
        console.error("[forge-viewer] loadDocumentNode failed:", err)
        this._showError("Couldn't load this view", "Pick another sheet or try refreshing.")
      })
  }

  // Custom-event bridge from `bim_pin_overlay_controller.js`. Resolves
  // the viewpoint by id (via the existing JSON show endpoint), maps
  // its persisted externalIds back to dbIds in the current model, and
  // calls `_restoreStateAsync` so the SDK race covered in Tier 3 stays
  // covered for pin-clicks too.
  async _onRestoreViewpointEvent(event) {
    const viewpointId = event && event.detail && event.detail.viewpointId
    if (!viewpointId) return
    if (!this._viewer || !this._currentModel) return
    if (!this.hasViewpointsUrlValue) return
    try {
      const res = await fetch(`${this.viewpointsUrlValue}/${viewpointId}`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const vp = await res.json()
      const mapping = await this._dbIdMapPromise()
      const objectSet = (vp.object_set || []).map((entry) => {
        if (!mapping) return entry
        const next = Object.assign({}, entry)
        const resolve = (arr) => Array.isArray(arr) ? arr.map(extId => mapping.extToDb[extId]).filter(v => v != null) : arr
        if (Array.isArray(entry.external_ids))      next.id       = resolve(entry.external_ids)
        if (Array.isArray(entry.external_isolated)) next.isolated = resolve(entry.external_isolated)
        if (Array.isArray(entry.external_hidden))   next.hidden   = resolve(entry.external_hidden)
        return next
      })
      const state = {
        viewport: vp.camera_state || {},
        objectSet: objectSet,
        cutplanes: vp.cutplanes || [],
        renderOptions: vp.render_options || {}
      }
      await this._restoreStateAsync(state)
    } catch (err) {
      console.warn("[forge-viewer] restore from pin failed:", err)
    }
  }

  // Public — wired from view buttons (Step 32: 2D / sheet swap).
  loadView(event) {
    const guid = event.params.viewGuid
    if (!guid || !this._loadedDocument || !this._viewer) return
    const node = this._loadedDocument.getRoot().findByGuid(guid)
    if (!node) return
    this._viewer.tearDown()
    this._viewer.setUp()
    this._loadModelNode(node)
    this._track("Viewer Extension Toggled", { extension_id: "load_view", guid: guid })
  }

  // § 3.5 Tier 7 Step 80 — Levels + Minimap extension toggles. The
  // extensions are auto-loaded with the SDK; these handlers flip the
  // visibility of their UI panels. Persist preference to localStorage
  // so a customer who likes the minimap on doesn't have to toggle it
  // every visit (a UserSetting column would be cleaner but adds a
  // migration on the polish path; keep blast radius small).
  toggleLevels(event) {
    if (event && event.preventDefault) event.preventDefault()
    if (!this._viewer) return
    const ext = this._viewer.getExtension("Autodesk.AEC.LevelsExtension")
    if (!ext) return
    const next = !this._levelsOn
    if (next) { try { ext.activate() } catch (_) {} }
    else      { try { ext.deactivate() } catch (_) {} }
    this._levelsOn = next
    try { localStorage.setItem("forge:levels", next ? "1" : "0") } catch (_) {}
    this._track("Viewer Extension Toggled", { extension_id: "levels", on: next })
  }

  toggleMinimap(event) {
    if (event && event.preventDefault) event.preventDefault()
    if (!this._viewer) return
    const ext = this._viewer.getExtension("Autodesk.AEC.Minimap3DExtension")
    if (!ext) return
    const next = !this._minimapOn
    if (next) { try { ext.activate() } catch (_) {} }
    else      { try { ext.deactivate() } catch (_) {} }
    this._minimapOn = next
    try { localStorage.setItem("forge:minimap", next ? "1" : "0") } catch (_) {}
    this._track("Viewer Extension Toggled", { extension_id: "minimap", on: next })
  }

  // § 3.5 Tier 7 Step 78 — mobile bottom-sheet handlers. Cycle
  // through saved viewpoints in order; the user's "next" tap restores
  // the next one in the list. Wraps at the end.
  cycleViewpointForward(event) {
    if (event && event.preventDefault) event.preventDefault()
    const items = this.hasViewpointListTarget
      ? this.viewpointListTarget.querySelectorAll("[data-viewpoint-id]")
      : []
    if (!items.length) return
    this._mobileViewpointIdx = ((this._mobileViewpointIdx || 0) + 1) % items.length
    const row = items[this._mobileViewpointIdx]
    const restoreBtn = row?.querySelector("[data-action*='restoreViewpoint']")
    restoreBtn?.dispatchEvent(new MouseEvent("click", { bubbles: true }))
  }

  // § 3.5 Tier 7 Step 78 — same code path the desktop toolbar uses.
  // The mobile button is the touch-friendly entry-point on viewports
  // where the SDK's GuiViewer3D toolbar isn't rendered.
  mobileCreateActionItem(event) {
    if (event && event.preventDefault) event.preventDefault()
    this._handleCreateActionItemFromViewer()
  }

  // Wait for `restoreState` to actually settle — without this, a
  // follow-up `setSelection`/`isolate` call clobbers the camera mid-
  // animation. Cited in the APS "Wait for restoreState to finish"
  // post (External References).
  _restoreStateAsync(state) {
    return new Promise((resolve) => {
      const Viewing = window.Autodesk && window.Autodesk.Viewing
      if (!this._viewer || !Viewing) return resolve()
      const eventName = Viewing.VIEWER_STATE_RESTORED_EVENT || "viewerStateRestoredEvent"
      let settled = false
      const onDone = () => {
        if (settled) return
        settled = true
        try { this._viewer.removeEventListener(eventName, onDone) } catch (_) {}
        resolve()
      }
      try {
        this._viewer.addEventListener(eventName, onDone)
      } catch (_) {
        // SDK older than 7.30 doesn't expose the event constant —
        // fall back to a tick-based guard.
      }
      this._viewer.restoreState(state, null, false)
      // Hard timeout in case the event never fires (older SDK).
      setTimeout(onDone, 800)
    })
  }

  // ── Selection (Step 31) ──────────────────────────────────────────────

  _wireSelectionEvents() {
    if (!this._viewer) return
    const Viewing = window.Autodesk.Viewing
    this._selectionHandler = (e) => this._onSelectionChanged(e)
    this._viewer.addEventListener(Viewing.SELECTION_CHANGED_EVENT, this._selectionHandler)
  }

  _onSelectionChanged(event) {
    const dbIds = event.dbIdArray || []
    if (dbIds.length === 0) {
      this._lastSelectionDbId = null
      this.selectionCountTarget.textContent = "No selection"
      this.selectionPanelTarget.innerHTML = `
        <p class="text-gray-500 text-[13px]">Click an element to see its name, external id, and parameters.</p>
      `
      return
    }
    this.selectionCountTarget.textContent = dbIds.length === 1 ? "1 element" : `${dbIds.length} elements`
    this._lastSelectionDbId = dbIds[0]

    if (!this._currentModel) return
    this._currentModel.getProperties(dbIds[0], (props) => {
      this._renderSelectionProps(props)
      this._track("Element Selected", {
        has_external_id: Boolean(props && props.externalId),
        selection_count: dbIds.length
      })
    }, (err) => {
      console.warn("[forge-viewer] getProperties failed:", err)
    })
  }

  _renderSelectionProps(props) {
    if (!props) {
      this.selectionPanelTarget.innerHTML = `
        <p class="text-rose-700 text-[13px]">Couldn't load element details.</p>
      `
      return
    }
    const externalId = props.externalId || ""
    const name = (props.name || "").toString()
    const params = Array.isArray(props.properties) ? props.properties.slice(0, 24) : []

    const escape = (s) => String(s).replace(/[<>"&]/g, (c) => ({
      "<": "&lt;", ">": "&gt;", "\"": "&quot;", "&": "&amp;"
    }[c]))

    const rows = params.map(p => `
      <tr class="border-b border-gray-100 last:border-0">
        <td class="py-1 pr-3 text-gray-500 text-[12px] align-top">${escape(p.displayCategory || "")} ${escape(p.displayName || "")}</td>
        <td class="py-1 text-gray-800 text-[12px] break-words">${escape(p.displayValue == null ? "" : p.displayValue)}</td>
      </tr>
    `).join("")

    this.selectionPanelTarget.innerHTML = `
      <div class="space-y-2">
        <div>
          <div class="text-[12px] text-gray-500">Name</div>
          <div class="text-[13px] text-gray-900">${escape(name) || "(unnamed)"}</div>
        </div>
        <div>
          <div class="text-[12px] text-gray-500">Element ID</div>
          <div class="flex items-center gap-2">
            <code class="text-[12px] text-gray-800 break-all">${escape(externalId)}</code>
            ${externalId ? `
              <button type="button" data-action="click->forge-viewer#copyExternalId"
                      data-forge-viewer-external-id-param="${escape(externalId)}"
                      class="text-[11px] px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50">Copy</button>
            ` : ""}
          </div>
        </div>
        ${rows ? `
          <div>
            <div class="text-[12px] text-gray-500 mt-2">Parameters</div>
            <div class="max-h-[260px] overflow-y-auto mt-1">
              <table class="w-full text-[12px]"><tbody>${rows}</tbody></table>
            </div>
          </div>
        ` : ""}
      </div>
    `
  }

  copyExternalId(event) {
    const id = event.params.externalId
    if (!id) return
    navigator.clipboard?.writeText(id).catch(() => {})
  }

  // ── Federation (Step 33) ─────────────────────────────────────────────

  toggleDiscipline(event) {
    if (!this._viewer || !this._currentModel) return
    const discipline = event.params.discipline
    const checked = event.target.checked
    // Resolve dbIds whose category-or-source name contains the discipline.
    // For federations, each loaded model can be hidden via hide/show; for
    // a single model we filter via search → isolation.
    const tree = this._currentModel.getInstanceTree && this._currentModel.getInstanceTree()
    if (!tree) return
    const matches = []
    this._currentModel.search(discipline, (ids) => {
      matches.push(...ids)
      if (checked) {
        this._viewer.show(matches, this._currentModel)
      } else {
        this._viewer.hide(matches, this._currentModel)
      }
      this._track("Viewer Extension Toggled", {
        extension_id: "discipline_toggle",
        discipline: discipline,
        on: checked
      })
    }, () => {}, ["Category", "Discipline", "Type Name"])
  }

  // ── Save / restore viewpoints (Steps 29, 30) ─────────────────────────

  openSaveDialog() {
    if (!this._viewer || !this.canSaveViewpointsValue) return
    this._viewer.getScreenShot(800, 480, (blobOrUrl) => {
      // Modern viewers return a data URL string; older returned a blob.
      let dataUrl = null
      if (typeof blobOrUrl === "string") {
        dataUrl = blobOrUrl
      } else if (blobOrUrl instanceof Blob) {
        const reader = new FileReader()
        reader.onload = () => {
          this._pendingScreenshotDataUrl = reader.result
          this._showSaveModal(reader.result)
        }
        reader.readAsDataURL(blobOrUrl)
        return
      }
      this._pendingScreenshotDataUrl = dataUrl
      this._showSaveModal(dataUrl)
    })
  }

  _showSaveModal(dataUrl) {
    if (this.hasViewpointThumbTarget && dataUrl) {
      this.viewpointThumbTarget.src = dataUrl
      this.viewpointPreviewTarget.classList.remove("hidden")
    }
    this.viewpointNameTarget.value = ""
    this.saveModalTarget.classList.remove("hidden")
    setTimeout(() => this.viewpointNameTarget.focus(), 50)
  }

  closeSaveDialog() {
    this.saveModalTarget.classList.add("hidden")
    this._pendingScreenshotDataUrl = null
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async confirmSave() {
    if (!this._viewer) return
    const name = this.viewpointNameTarget.value.trim()
    if (!name) {
      this.viewpointNameTarget.focus()
      return
    }
    const state = this._viewer.getState({ viewport: true, objectSet: true, renderOptions: true })
    const objectSet = await this._mapObjectSetToExternalIds(state.objectSet || [])

    this.confirmSaveButtonTarget.disabled = true
    try {
      const res = await fetch(this.viewpointsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        credentials: "same-origin",
        body: JSON.stringify({
          viewpoint: {
            name: name,
            camera_state: state.viewport || {},
            object_set: objectSet,
            cutplanes: state.cutplanes || [],
            render_options: state.renderOptions || {},
            thumbnail: this._pendingScreenshotDataUrl
          }
        })
      })
      if (!res.ok) {
        const errPayload = await res.json().catch(() => ({}))
        alert(errPayload.message || "Couldn't save viewpoint.")
        return
      }
      const json = await res.json()
      this._prependViewpointRow(json)
      this.closeSaveDialog()
    } catch (err) {
      console.error("[forge-viewer] save viewpoint failed:", err)
      alert("Couldn't save viewpoint.")
    } finally {
      this.confirmSaveButtonTarget.disabled = false
    }
  }

  // Object-set persistence: server stores externalId arrays so a
  // re-translate doesn't orphan the viewpoint (Architecture Decision
  // #3). On save we resolve dbIds → externalIds via the model's
  // getExternalIdMapping cache.
  async _mapObjectSetToExternalIds(objectSet) {
    if (!this._currentModel) return objectSet
    const mapping = await this._dbIdMapPromise()
    if (!mapping) return objectSet
    const dbIdToExternalId = mapping.dbToExt
    return objectSet.map((entry) => {
      const next = Object.assign({}, entry)
      const map = (arr) => Array.isArray(arr) ? arr.map(id => dbIdToExternalId[id]).filter(Boolean) : arr
      if (entry.id)       next.external_ids       = map(entry.id)
      if (entry.isolated) next.external_isolated  = map(entry.isolated)
      if (entry.hidden)   next.external_hidden    = map(entry.hidden)
      return next
    })
  }

  _dbIdMapPromise() {
    if (this._dbIdMap) return Promise.resolve(this._dbIdMap)
    return new Promise((resolve) => {
      this._currentModel.getExternalIdMapping((extToDb) => {
        const dbToExt = {}
        Object.keys(extToDb || {}).forEach((extId) => {
          dbToExt[extToDb[extId]] = extId
        })
        this._dbIdMap = { extToDb: extToDb || {}, dbToExt }
        resolve(this._dbIdMap)
      }, () => resolve(null))
    })
  }

  // Restore a viewpoint — wired from row buttons.
  async restoreViewpoint(event) {
    if (!this._viewer || !this._currentModel) return
    const id = event.params.viewpointId
    if (!id) return
    const res = await fetch(`${this.viewpointsUrlValue}/${id}`, {
      headers: { "Accept": "application/json" },
      credentials: "same-origin"
    })
    if (!res.ok) return
    const vp = await res.json()
    const mapping = await this._dbIdMapPromise()
    const objectSet = (vp.object_set || []).map((entry) => {
      if (!mapping) return entry
      const next = Object.assign({}, entry)
      const resolve = (arr) => Array.isArray(arr) ? arr.map(extId => mapping.extToDb[extId]).filter(v => v != null) : arr
      if (Array.isArray(entry.external_ids))      next.id       = resolve(entry.external_ids)
      if (Array.isArray(entry.external_isolated)) next.isolated = resolve(entry.external_isolated)
      if (Array.isArray(entry.external_hidden))   next.hidden   = resolve(entry.external_hidden)
      return next
    })
    const state = {
      viewport: vp.camera_state || {},
      objectSet: objectSet,
      cutplanes: vp.cutplanes || [],
      renderOptions: vp.render_options || {}
    }
    await this._restoreStateAsync(state)
    this._track("Viewpoint Restored", { viewpoint_id: id })
  }

  async deleteViewpoint(event) {
    const id = event.params.viewpointId
    if (!id) return
    // No native confirm() — CLAUDE.md rule. Tier 3 ships click-to-delete;
    // a future polish pass can swap in an inline confirm modal if users
    // misclick frequently.
    const res = await fetch(`${this.viewpointsUrlValue}/${id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": this.csrfTokenValue, "Accept": "application/json" },
      credentials: "same-origin"
    })
    if (!res.ok) return
    const row = this.viewpointListTarget.querySelector(`[data-viewpoint-id="${id}"]`)
    if (row) row.remove()
  }

  _prependViewpointRow(json) {
    if (!this.hasViewpointListTarget) return
    const empty = this.viewpointListTarget.querySelector("[data-empty]")
    if (empty) empty.remove()
    const html = `
      <div class="px-3 py-2 flex items-start gap-2" data-viewpoint-id="${json.id}">
        <button type="button"
                data-action="click->forge-viewer#restoreViewpoint"
                data-forge-viewer-viewpoint-id-param="${json.id}"
                class="flex-1 text-left flex items-start gap-2 hover:bg-gray-50 rounded-lg p-1 -m-1">
          ${json.thumbnail_url ? `<img src="${json.thumbnail_url}" class="w-12 h-9 rounded object-cover border border-gray-200" alt="">` :
            `<div class="w-12 h-9 rounded bg-gray-100 border border-gray-200"></div>`}
          <div class="min-w-0">
            <div class="text-[13px] text-gray-900 truncate">${json.name}</div>
            <div class="text-[11px] text-gray-500">just now</div>
          </div>
        </button>
        <button type="button" title="Remove"
                data-action="click->forge-viewer#deleteViewpoint"
                data-forge-viewer-viewpoint-id-param="${json.id}"
                class="p-1 rounded hover:bg-rose-50 text-rose-700">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `
    this.viewpointListTarget.insertAdjacentHTML("afterbegin", html)
  }

  // ── Streaming meter (Step 35) ────────────────────────────────────────

  _startSessionTimer() {
    if (this._sessionTimer) return
    this._sessionId = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}`
    this._sessionStartedAt = Date.now()
    this._lastTickAt = Date.now()
    this._sessionTimer = setInterval(() => this._sendSessionTick(60), 60_000)
  }

  _stopSessionTimer({ final = false } = {}) {
    if (this._sessionTimer) {
      clearInterval(this._sessionTimer)
      this._sessionTimer = null
    }
    if (final && this._lastTickAt) {
      const delta = Math.max(0, Math.min(600, Math.round((Date.now() - this._lastTickAt) / 1000)))
      if (delta > 0) this._sendSessionTick(delta, true)
    }
  }

  _onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this._stopSessionTimer({ final: true })
    } else if (document.visibilityState === "visible" && this._currentModel && !this._sessionTimer) {
      this._startSessionTimer()
    }
  }

  _sendSessionTick(deltaSeconds, final = false) {
    if (!this.hasSessionUrlValue) return
    this._lastTickAt = Date.now()
    const body = new URLSearchParams()
    body.append("delta_seconds", String(deltaSeconds))
    body.append("session_id", this._sessionId || "")
    if (final) body.append("final", "1")
    fetch(this.sessionUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.csrfTokenValue
      },
      credentials: "same-origin",
      body: body.toString(),
      keepalive: final // ensure the final tick lands as the page closes
    }).catch(() => {})
  }

  // ── UI helpers ───────────────────────────────────────────────────────

  _setStatus(title, body) {
    if (this.hasStatusTitleTarget) this.statusTitleTarget.textContent = title
    if (this.hasStatusBodyTarget) this.statusBodyTarget.textContent = body
  }

  _hideStatus() {
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
    if (this.hasContainerTarget) this.containerTarget.classList.remove("hidden")
  }

  _showError(title, body) {
    if (this.hasStatusTarget) this.statusTarget.classList.remove("hidden")
    if (this.hasContainerTarget) this.containerTarget.classList.add("hidden")
    this._setStatus(title, body)
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.remove("hidden")
  }

  retry() {
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.add("hidden")
    this._setStatus("Loading viewer…", "Streaming the SVF2 derivative from Autodesk.")
    this._bootSdk()
  }

  _track(name, props) {
    try {
      if (window.mixpanel && typeof window.mixpanel.track === "function") {
        window.mixpanel.track(name, Object.assign({ source: "forge_viewer" }, props || {}))
      }
    } catch (_) {}
  }

  // ── § 3.5 Tier 4 — Action Item from 3D ──────────────────────────────

  _canAuthorActionItems() {
    return Boolean(
      this.hasPinPrepareUrlValue &&
      this.pinPrepareUrlValue &&
      this.canCreateActionItemsValue
    )
  }

  // Toolbar-button click handler. Builds the four-part capture
  // (selection → externalIds, viewer state, screenshot, world-space
  // anchor) and POSTs to the prepare endpoint, then navigates to the
  // composer URL the server returns. Failure modes communicate inline
  // through the existing status overlay so the user isn't dropped into
  // an alert() (CLAUDE.md "no browser confirm()" rule, in spirit); the
  // most common is "no selection" which we guard first.
  async _handleCreateActionItemFromViewer() {
    if (!this._viewer || !this._currentModel) return
    const dbIds = this._viewer.getSelection() || []
    if (dbIds.length === 0) {
      this._flashStatus("Pick at least one element",
                       "Click a duct, beam, or fixture, then try again.")
      return
    }

    let externalIds = []
    try {
      const map = await this._dbIdMapPromise()
      if (map && map.dbToExt) {
        externalIds = dbIds.map(id => map.dbToExt[id]).filter(Boolean)
      }
    } catch (_) {
      // Fallback below — without externalIds we can't author a pin.
    }
    if (externalIds.length === 0) {
      this._flashStatus("Couldn't resolve element identifiers",
                       "Try a different element or refresh the viewer.")
      return
    }

    const state = this._viewer.getState({ viewport: true, objectSet: true, renderOptions: true })

    // Map dbIds in the captured object_set to externalIds (Architecture
    // Decision #3) so the saved viewpoint we mint here survives a
    // future re-translate in the same way Tier 3's user-saved
    // viewpoints do.
    const objectSet = await this._mapObjectSetToExternalIds(state.objectSet || [])

    const screenshot = await this._captureScreenshotDataUrl(800, 600).catch(() => null)
    const position = this._computeWorldAnchor(dbIds[0])

    // Server-authoritative "Action Item From 3D Started" fires inside
    // the prepare endpoint (the one that actually does the work);
    // client-side here we don't double-fire — keeps the event
    // count honest.

    const body = {
      context: {
        element_external_ids: externalIds,
        position: position || {},
        thumbnail: screenshot,
        state: {
          viewport: state.viewport || {},
          object_set: objectSet,
          cutplanes: state.cutplanes || [],
          render_options: state.renderOptions || {}
        }
      }
    }

    try {
      const res = await fetch(this.pinPrepareUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        credentials: "same-origin",
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}))
        this._flashStatus("Couldn't open the action-item composer",
                          payload.message || "Try again, or refresh the viewer.")
        return
      }
      const json = await res.json()
      if (!json.composer_url) {
        this._flashStatus("Couldn't open the action-item composer",
                          "The server didn't return a target URL.")
        return
      }
      // Plain navigation — composer is a full-page form. The captured
      // viewpoint id rides along in the URL so a back-button-then-
      // retry doesn't double-charge the prepare endpoint.
      window.location.href = json.composer_url
    } catch (err) {
      console.error("[forge-viewer] action-item prepare failed:", err)
      this._flashStatus("Couldn't reach the server",
                        "Check your connection and try again.")
    }
  }

  // Flash a transient inline status into the viewer chrome — replaces
  // alert() / confirm() (CLAUDE.md). Auto-dismisses after ~2s and
  // restores the viewer container.
  _flashStatus(title, body) {
    if (!this.hasStatusTarget || !this.hasContainerTarget) return
    this._setStatus(title, body)
    this.statusTarget.classList.remove("hidden")
    this.containerTarget.classList.add("hidden")
    if (this._statusFlashTimer) clearTimeout(this._statusFlashTimer)
    this._statusFlashTimer = setTimeout(() => {
      this.statusTarget.classList.add("hidden")
      this.containerTarget.classList.remove("hidden")
    }, 2200)
  }

  // Wraps `viewer.getScreenShot` in a Promise that resolves to a data
  // URL regardless of whether the SDK calls back with a string (modern)
  // or a Blob (older). Mirrors `openSaveDialog` but doesn't pop a
  // modal.
  _captureScreenshotDataUrl(width, height) {
    return new Promise((resolve, reject) => {
      try {
        this._viewer.getScreenShot(width, height, (blobOrUrl) => {
          if (typeof blobOrUrl === "string") return resolve(blobOrUrl)
          if (blobOrUrl instanceof Blob) {
            const reader = new FileReader()
            reader.onload  = () => resolve(reader.result)
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(blobOrUrl)
          } else {
            resolve(null)
          }
        })
      } catch (err) {
        reject(err)
      }
    })
  }

  // World-space anchor = centroid of the element's bounding box.
  // Returning `{}` (rather than null) when the SDK can't resolve a
  // bbox keeps the server-side serializer honest — the column
  // accepts an empty hash. The pin overlay falls back to "0,0,0"
  // visually; pins without a position render at the model origin
  // until a follow-up re-anchor pass fixes them.
  _computeWorldAnchor(dbId) {
    if (!this._currentModel || dbId == null) return null
    try {
      const tree = this._currentModel.getInstanceTree && this._currentModel.getInstanceTree()
      if (!tree) return null
      const box = new (window.THREE && window.THREE.Box3 ? window.THREE.Box3 : Object)()
      if (typeof box.makeEmpty === "function") box.makeEmpty()
      const bounds = (typeof tree.getNodeBox === "function")
        ? (() => {
            const b = new Float32Array(6)
            tree.getNodeBox(dbId, b)
            return [b[0], b[1], b[2], b[3], b[4], b[5]]
          })()
        : null
      if (!bounds) return null
      return {
        x: (bounds[0] + bounds[3]) / 2,
        y: (bounds[1] + bounds[4]) / 2,
        z: (bounds[2] + bounds[5]) / 2
      }
    } catch (_) {
      return null
    }
  }
}
